import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { Card, CardContent, CardMedia, Grid } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { NavigatorData, ProductId, Products } from './NavigatorData';
import { DeltaXMLColors, MyDeltaColors } from './styling/DeltaColors';
import SaaSProduct from './SaaSProduct';
import { ProfileDetails } from './ProfileContent';
import { SubsTrialData } from './SaaSConfirmTrial';
import { TrialProducts } from './Paperbase';
import { WebAuth } from 'auth0-js';

export interface SaaSEntry {
  id: TrialProducts,
  label?: string,
  icon: string,
  productDesc: string,
  link?: string
}

const SaasProductInfo: SaaSEntry[] = [
  {
    id: ProductId.ccS1000D, 
    icon: "/S1000D_MyDelta.svg",
    label: 'Content Compare S1000D',
    productDesc: `
    A REST API for comparing S1000D content.\nThe result is Data Module 'B' with
    markup explaining text-only differences from Data Module 'A'.`
  },
  {
    id: ProductId.ccJATS, 
    icon: "/JATS_MyDelta.svg",
    label: 'Content Compare JATS',
    productDesc: `
    A REST API for comparing JATS content.\nThe result is JATS 'B' with
    markup explaining text-only differences from JATS 'A'.`
  },
  {
    id: ProductId.ccBITS, 
    icon: "/BITS_MyDelta.svg",
    label: 'Content Compare BITS',
    productDesc: `
    A REST API for comparing BITS content.\nThe result is BITS 'B' with
    markup explaining text-only differences from BITS 'A'.`
  },
  {
    id: ProductId.ccNISOSTS, 
    icon: "/STS_MyDelta.svg",
    label: 'Content Compare NISO-STS',
    productDesc: `
    A REST API for comparing NISO-STS content.\nThe result is NISO-STS 'B' with
    markup explaining text-only differences from NISO-STS 'A'.`
  },
  {
    id: ProductId.ccXSLFO, 
    icon: "/XSL-FO_MyDelta.svg",
    label: 'Content Compare XSL-FO',
    productDesc: `
    A REST API for comparing XSL-FO content.\nThe result is XSL-FO 'B' with
    markup explaining text-only differences from XSL-FO 'A'.`
  }

];

export enum SaaSTrialStatus {
  Unknown,
  RequestingTrial,
  Ongoing,
  PaidOngoing,
  Permitted,
  NotPermitted,
  Error
  // SaaS status for non trials is managed elsewhere
}

export interface SaasTrialStatusCollection {
  [ProductId.ccS1000D]: SaaSTrialStatus,
  [ProductId.ccJATS]: SaaSTrialStatus,
  [ProductId.ccBITS]: SaaSTrialStatus, 
  [ProductId.ccNISOSTS]: SaaSTrialStatus,
  [ProductId.ccXSLFO]: SaaSTrialStatus
}

export interface SaasDealIdCollection {
  [ProductId.ccS1000D]: string | undefined,
  [ProductId.ccJATS]: string | undefined,
  [ProductId.ccBITS]: string | undefined,
  [ProductId.ccNISOSTS]: string | undefined,
  [ProductId.ccXSLFO]: string | undefined
}

export interface SaasAuthProductCollection {
  [ProductId.ccS1000D]: WebAuth,
  [ProductId.ccJATS]: WebAuth,
  [ProductId.ccBITS]: WebAuth,
  [ProductId.ccNISOSTS]: WebAuth,
  [ProductId.ccXSLFO]: WebAuth
}

export function resetSaasTrialStatus(status: SaaSTrialStatus): SaasTrialStatusCollection {
  return { 
    [ProductId.ccS1000D]: status,
    [ProductId.ccJATS]: status,
    [ProductId.ccBITS]: status,
    [ProductId.ccNISOSTS]: status,
    [ProductId.ccXSLFO]: status
  }
}

export function initDealIdCollection(): SaasDealIdCollection {
  return { 
    [ProductId.ccS1000D]: undefined,
    [ProductId.ccJATS]: undefined,
    [ProductId.ccBITS]: undefined,
    [ProductId.ccNISOSTS]: undefined, 
    [ProductId.ccXSLFO]: undefined
  }
}

const styles = (theme: Theme) =>
  createStyles({
    paper: {
      maxWidth: 1600,
      margin: 'auto',
      overflow: 'hidden'
    },
    searchBar: {
      borderBottom: '1px solid ' + MyDeltaColors.MERCURY,
      backgroundColor: DeltaXMLColors.DARK_BACKGROUND,
    },
    innerProductCard: {
      display: 'flex',
      width: 720,
      backgroundColor: DeltaXMLColors.CONCRETE,
      height: '200px',
      margin: 10
    },
    productsWrapper: {
      margin: 'auto',
      padding: 20
    },
    cover: {
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      verticalAlign: 'middle',
      width: '230px'
    },
    urlNormalization: {
      textDecoration: 'none',
    },
    cardHeader: {
      color: DeltaXMLColors.OXFORD_BLUE
    },
    linkStyle: {
      textDecoration: 'none',
      position: 'relative'
    },
  });

export interface ContentProps extends WithStyles<typeof styles> {
  profileDetails: ProfileDetails,
  saasTrialStatusCollection: SaasTrialStatusCollection,
  startSubsTrial: (product: TrialProducts, trialData: SubsTrialData) => void,
  openSubscriptionDeal: (product: TrialProducts) => void;
}

function SaasContent(props: ContentProps) {
  const { classes, profileDetails, saasTrialStatusCollection, startSubsTrial, openSubscriptionDeal } = props;

  return (
    <Paper className={classes.paper}>
      <AppBar className={classes.searchBar} position="static" elevation={0}>
        <Toolbar>
          <Typography variant="h4">SaaS Products</Typography>
        </Toolbar>
      </AppBar>
      <div className={classes.productsWrapper}>
        <Grid container spacing={1} direction="row" alignItems="center" justify="center">
        <a href={process.env.REACT_APP_DELTAJSON_APP_URL} className={classes.linkStyle} target="_blank" rel="noreferrer">
            <Card className={classes.innerProductCard}>
              <div className={classes.cover}>
                <CardMedia component="img"
                  image="/DeltaJSONIcon_White.svg"
                  title="DeltaJSON" />
              </div>
              <div>
                <CardContent>
                  <Typography gutterBottom variant="h5" className={classes.cardHeader}>
                    DeltaJSON
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Differencing, merging and patching services for JSON.
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Service provides Web app and REST API for viewing, creating, running and managing tasks.
                  </Typography>
                </CardContent>
              </div>
            </Card>
          </a>
          {SaasProductInfo.map((item: SaaSEntry) => {
            return (<SaaSProduct key={item.id} productInfo={item} profileDetails={profileDetails} saasTrialStatus={saasTrialStatusCollection[item.id]} startSubsTrial={startSubsTrial} openSubscriptionDeal={openSubscriptionDeal} />)
          })}
        </Grid>
      </div>
    </Paper>
  );
}

export default withStyles(styles)(SaasContent);
